import React, { useState, useEffect } from "react";
import Logger from "../../../services/Logger";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Item from "../../components/Item";
import IngestService from "./IngestService";
import ConfirmDialog from "../../components/ConfirmDialog";

dayjs.extend(customParseFormat);

const AgentWikiOnThisDay = ({ setting, onRefresh }) => {
    const now = dayjs();
    const [wikipediaMonth, setWikipediaMonth] = useState(now.month() + 1);
    const [wikipediaDay, setWikipediaDay] = useState(now.date());
    const [wikipediaIngesting, setWikipediaIngesting] = useState(false);
    const [wikipediaError, setWikipediaError] = useState(null);
    const [wikipediaSuccess, setWikipediaSuccess] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Calculate next day from last ingested date
    useEffect(() => {
        if (setting?.value) {
            try {
                // Parse MM/DD format and calculate next day
                const currentYear = dayjs().year();
                const nextDay = dayjs(`${currentYear}/${setting.value}`, "YYYY/MM/DD")
                    .add(1, 'day');
                
                setWikipediaMonth(nextDay.month() + 1);
                setWikipediaDay(nextDay.date());
            } catch (err) {
                Logger.error("Error parsing last ingested date:", err);
                // Fallback to current date if parsing fails
                setWikipediaMonth(now.month() + 1);
                setWikipediaDay(now.date());
            }
        }
    }, [setting]);

    const handleWikipediaIngest = () => {
        // Validate inputs
        if (wikipediaMonth < 1 || wikipediaMonth > 12) {
            setWikipediaError("Month must be between 1 and 12");
            return;
        }
        if (wikipediaDay < 1 || wikipediaDay > 31) {
            setWikipediaError("Day must be between 1 and 31");
            return;
        }

        setShowConfirmDialog(true);
    };

    const confirmWikipediaIngest = async () => {
        try {
            setWikipediaIngesting(true);
            setWikipediaError(null);
            setWikipediaSuccess(null);

            const result = await IngestService.ingestWikipediaArticles(wikipediaMonth, wikipediaDay);
            
            if (result.success) {
                const ingestedCount = result.data?.ingested || 0;
                setWikipediaSuccess(`${ingestedCount} draft puzzle${ingestedCount !== 1 ? 's' : ''} have been created`);
                
                // Refresh all settings after ingest
                if (onRefresh) {
                    await onRefresh();
                }
            } else {
                setWikipediaError(result.error);
            }
        } catch (err) {
            Logger.error("Error ingesting Wikipedia articles:", err);
            setWikipediaError(err.message || "Failed to ingest Wikipedia articles");
        } finally {
            setWikipediaIngesting(false);
        }
    };

    return (
        <Item sx={{ height: '100%', width: '100%' }}>
            {setting ? (
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                            Wikipedia On This Day
                        </Typography>
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                    Last Ingested Date:
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1 }}>
                                    {setting.value || "N/A"}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                <TextField
                                    label="Month"
                                    type="number"
                                    value={wikipediaMonth}
                                    onChange={(e) => setWikipediaMonth(parseInt(e.target.value) || 1)}
                                    inputProps={{ min: 1, max: 12 }}
                                    sx={{ width: 100 }}
                                    size="small"
                                />
                                <TextField
                                    label="Day"
                                    type="number"
                                    value={wikipediaDay}
                                    onChange={(e) => setWikipediaDay(parseInt(e.target.value) || 1)}
                                    inputProps={{ min: 1, max: 31 }}
                                    sx={{ width: 100 }}
                                    size="small"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleWikipediaIngest}
                                    disabled={wikipediaIngesting}
                                    sx={{ minWidth: 140 }}
                                >
                                    {wikipediaIngesting ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Progressing...
                                        </>
                                    ) : (
                                        "Execute"
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                        {wikipediaSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {wikipediaSuccess}
                            </Alert>
                        )}
                        {wikipediaError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {wikipediaError}
                            </Alert>
                        )}
                        <Alert severity="info">
                            Scheduled: 10:00 AM every day
                        </Alert>
                    </CardContent>
                </Card>
            ) : (
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                            Wikipedia On This Day
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No Wikipedia ingest setting found
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <ConfirmDialog
                open={showConfirmDialog}
                setOpen={setShowConfirmDialog}
                title="Confirm Wikipedia Articles Ingest"
                message={`Are you sure you want to ingest Wikipedia articles for month ${wikipediaMonth}, day ${wikipediaDay}?`}
                callback={confirmWikipediaIngest}
            />
        </Item>
    );
};

export default AgentWikiOnThisDay;
